import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useId } from 'react';
import styles from './ui.module.css';

interface FieldProps {
  label: string;
  error?: string;
  hint?: ReactNode;
  children: (id: string, hasError: boolean) => ReactNode;
}

/** Wires the label, the control and its error message together by id. */
export function Field({ label, error, hint, children }: FieldProps) {
  const id = useId();

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      {children(id, Boolean(error))}
      {error && <p className={styles.errorText}>{error}</p>}
      {!error && hint && <div className={styles.hint}>{hint}</div>}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { hasError?: boolean; compact?: boolean };

export function Input({ hasError, compact, className, ...rest }: InputProps) {
  return <input {...rest} className={controlClass(hasError, className, compact)} />;
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & { hasError?: boolean;
  /** Toolbar size — same 36px box as a Button. */
  compact?: boolean };

export function Select({ hasError, compact, className, children, ...rest }: SelectProps) {
  return (
    <select {...rest} className={controlClass(hasError, className, compact)}>
      {children}
    </select>
  );
}

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { hasError?: boolean };

export function Textarea({ hasError, className, ...rest }: TextareaProps) {
  return <textarea {...rest} className={controlClass(hasError, className)} />;
}

function controlClass(
  hasError: boolean | undefined,
  extra: string | undefined,
  compact = false,
): string {
  return [
    styles.control,
    compact ? styles.controlCompact : null,
    hasError ? styles.controlError : null,
    extra,
  ]
    .filter(Boolean)
    .join(' ');
}
